import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import type { SalaryBand } from '../../services/salaryBandsApi';
import { ratingGrantRows } from './ratingEquity';

const euro = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

export function RatingGrantModal({ band, onClose }: { band: SalaryBand; onClose: () => void }) {
  const rows = ratingGrantRows(band.level, band.average);
  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,44rem)] gap-5 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-xl !font-semibold">
            Rating-based grant (best estimate) · {band.identifier || `Level ${band.level}`}
          </ModalTitle>
        </ModalHeader>
        <p className="text-sm text-iron">
          The two grant values show company performance at 70% and 130% of target for each average
          performance rating. They are separate scenarios, not a continuous range. Values are rounded
          down to full €100.
        </p>
        <div className="overflow-x-auto border border-pearl">
          <table className="min-w-full text-sm">
            <thead className="bg-snow text-left text-xs uppercase tracking-wide text-iron">
              <tr>
                <th scope="col" className="px-3 py-3">Average performance rating</th>
                <th scope="col" className="px-3 py-3 text-right">Grant at 70% company performance</th>
                <th scope="col" className="px-3 py-3 text-right">Grant at 130% company performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pearl">
              {rows.map((row) => (
                <tr key={row.rating}>
                  <td className="px-3 py-3 font-medium">{row.rating}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{euro.format(row.minGrant)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{euro.format(row.maxGrant)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-iron">
          Rating-based grants depend on your average rating from two performance cycles and on
          company performance. Read the{' '}
          <a
            className="font-medium text-trustedBlue underline"
            href="https://finn.atlassian.net/wiki/spaces/HR/pages/3168108584/Guide+to+our+Employee+Equity+Program"
            target="_blank"
            rel="noopener noreferrer"
          >
            Employee Equity Program Guide
          </a>{' '}
          for details.
        </p>
      </ModalContent>
    </Modal>
  );
}
